import { h, Component } from 'preact';
import { Text } from 'preact-i18n';
import util from 'preact-util';
import { observer } from 'mobx-preact';

/*
 * Triggers face DETECTION once per profile image. The backend endpoint
 * (POST /api/faces/detect) persists the result server-side, so this
 * component only kicks it off and then reloads the user so the store
 * reflects the stored result. Detection only — no biometric template is
 * created, and this component never writes the result itself.
 * See docs/superpowers/specs/2026-08-17-face-recognition-design.md
 */

/*
 * Every profile-image entry that could end up on screen, in the order the
 * backend would pick them.
 *
 * user.image FIRST is not cosmetic: musher-util's getImageEntry prefers
 * user.image whenever it has an s3SmallLink, and all nine emit sites call it
 * with no `priority`. Iterating only user.images would spend a Rekognition call
 * on a photo nobody sees while the displayed avatar stayed boxless forever —
 * and, because that entry never gains a faceDetection, needsDetection would stay
 * true for it on every single profile-edit mount. user.image is real data:
 * users/patch-admin-user.js and post-admin-user.js both write it, and legacy
 * rows carry it. The backend handles this case already (applyDetection).
 *
 * Deduped by newFilename — image and images[0] are frequently the same photo,
 * and one detection covers both because applyDetection writes every matching
 * entry. Entries without a newFilename are kept: they cannot be identified as
 * duplicates, needsDetection already rejects them, and dropping them would lose
 * a "no face" warning.
 */
export function detectionCandidates(user = {}) {
    const images = Array.isArray(user.images) ? user.images : [];
    return [user.image, ...images].filter((entry, index, list) => {
        if (!entry) {
            return false;
        }
        if (!entry.newFilename) {
            return true;
        }
        return list.findIndex(e => e && e.newFilename === entry.newFilename) === index;
    });
}

export function needsDetection(image) {
    if (!image || !image.newFilename) {
        return false;
    }
    return !image.faceDetection;
}

/*
 * The entry the avatar actually shows. Mirrors backend musher-util
 * getImageEntry({ user, priority: 'user' }) and webapp util.getImage: user.image
 * wins when it has an s3SmallLink, otherwise images[0] when it has one. The
 * "no face" warning is about the avatar being cropped oddly, so it must look at
 * this entry only — a room photo or a poster further down the gallery is fine.
 */
export function avatarEntry(user = {}) {
    if (user.image && user.image.s3SmallLink) {
        return user.image;
    }
    const images = Array.isArray(user.images) ? user.images : [];
    if (images[0] && images[0].s3SmallLink) {
        return images[0];
    }
    return null;
}

// Warning state for ONE image (the avatar, see avatarEntry):
// 'none'   = detection has not run, or ran and failed; say nothing.
// 'noFace' = detection ran and found no face; warn.
// 'ok'     = detection ran and found a face.
export function warningFor(image) {
    const detection = image && image.faceDetection;
    if (!detection || typeof detection.faceCount !== 'number') {
        return 'none';
    }
    return detection.faceCount === 0 ? 'noFace' : 'ok';
}

@observer
class ProfileImageFaceCheck extends Component {
    componentDidMount() {
        this.runDetection();
    }

    componentDidUpdate() {
        this.runDetection();
    }

    // No-request-storm guarantee rests on THREE mechanisms together — none of
    // them optional:
    //   (a) this.inFlight — set synchronously before the first `await`, so a
    //       second call re-entering before the fetch resolves (e.g. mount
    //       immediately followed by an update) is a no-op.
    //   (b) `stores` (and therefore `this.props.stores`) staying a
    //       referentially STABLE object all the way from app.js — required
    //       for (c) below. If an ancestor ever passes a freshly-constructed
    //       `stores` object (e.g. `stores={{...this.props.stores, x: 1}}`),
    //       this invariant breaks silently.
    //   (c) mobx-preact's implicit shouldComponentUpdate (shallow prop
    //       compare) skipping re-renders — and therefore skipping the
    //       componentDidUpdate -> runDetection call — when `stores` didn't
    //       change. If (b) is violated, (c) stops protecting and, on a page
    //       where detection is persistently failing, every re-render fires a
    //       fresh POST — a request storm (a) alone cannot catch, since (a)
    //       only blocks re-entrant calls for the SAME in-progress request.
    runDetection = async () => {
        const { userStore } = this.props.stores;
        const { user = {} } = userStore;
        const target = detectionCandidates(user).find(needsDetection);
        if (!target || this.inFlight === target.newFilename) {
            return;
        }
        this.inFlight = target.newFilename;

        const response = await util.fetchApi('/api/faces/detect', { method: 'POST' }, {
            imageKey: target.newFilename,
        });

        // The endpoint persisted the result; reload so the store reflects it.
        // forceLoad is REQUIRED: getInfo() short-circuits for 300s (user.js:1143)
        // and would silently skip the reload, leaving the box invisible until
        // the next cold load. A failed detection stored nothing and is retried
        // on a later visit.
        if (response?.status === 200 && response.data) {
            await userStore.getInfo({ forceLoad: true });
        }
        this.inFlight = null;
    }

    render() {
        const { userStore } = this.props.stores;
        const { user = {} } = userStore;
        const hasNoFace = warningFor(avatarEntry(user)) === 'noFace';

        if (!hasNoFace) {
            return null;
        }
        return (
            <div class='alert alert-warning py-2 px-3 mt-2 mb-0 small'>
                <i class='fa-duotone fa-user-slash me-2' />
                <Text id='profile.noFaceInImage'>
                    We could not find a face in this image. Your avatar may be cropped oddly.
                </Text>
            </div>
        );
    }
}

export default ProfileImageFaceCheck;
